import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  Price,
  ProductModifier,
  ProductModifierGroup,
  ProductModifierValidation,
  ProductVariation,
  Restaurant,
  RestaurantProduct,
  WorkDay
} from 'src/app/core/models/restaurant.model';
import 'hammerjs';
import {MenuItem, MenuItemData, MenuItemForPayment} from 'src/app/core/models/cart-item.model';
import {RestaurantStore} from 'src/app/core/stores/restaurant.store';
import {IonContent, ModalController, Platform} from '@ionic/angular';
import {CartStore} from 'src/app/core/stores/cart.store';
import {PaymentStore} from '../../../core/stores/payment.store';
import {ItemValidateService} from '../../../core/services/item-validate.service';
import { fromEvent } from 'rxjs';
import {takeWhile} from 'rxjs/operators';
import {FormatDataService} from 'src/app/core/services/format-data.service';
import {Router} from '@angular/router';
import { ModifierSelectionType } from '../../../core/enums/util.enum';
import * as _ from 'lodash';
import {EstimateOrder} from '../../../core/models/order.model';
import {ItemService} from '../../../core/services/item.service';
import {isModifierSoldOut} from '../../../core/dto/restaurant.dto';
import {DIETARY_LABEL, DietaryLabel} from '../../../core/constants/item.const';
import {MatDialog} from '@angular/material/dialog';
import {ThrottlingConfirmationComponent} from './throttling-confirmation/throttling-confirmation.component';

@Component({
  selector: 'app-product-modal',
  templateUrl: './product-modal.component.html',
  styleUrls: ['./product-modal.component.scss']
})
export class ProductModalComponent implements OnInit, OnDestroy {
  @Input() defaultProduct: RestaurantProduct;
  @Input() restaurant: Restaurant;
  @Input() hoursOfOperation: WorkDay[];
  @Input() locationId: string;
  @Input() currencySign = '$';
  @Input() isClosed: boolean;
  @Input() isRestaurantHaveEnabledWorkDays: boolean;
  @Input() allowScheduledPickups: boolean;
  @Input() cartProduct: MenuItemForPayment;
  @Input() isMobileEnabled: boolean;
  @Input() isModal: boolean;
  @Input() enforceInventory: boolean;
  @Output() close = new EventEmitter<boolean>();
  @Output() addItem = new EventEmitter<MenuItemForPayment>();
  @ViewChild(IonContent, { static: true }) public content: IonContent;

  private firstVariationIndex = 0;
  private readonly quantityThreshHold = 1;
  private readonly defaultQuantity = 1;

  public readonly truncateDescriptionLength = 3;

  public isDefaultTax = false;
  public isEdit = false;
  public variations: any = null;
  public variationsInvalidMessage = '';
  public chosenVariation: ProductVariation;
  public showInvalidMessage = false;
  public modifiersValidations: ProductModifierValidation[] = [];
  public specialInstructions: string;
  public touchSwipeElement = true;
  public isSgtPepps = false;
  public product: RestaurantProduct;
  public dietaryLabels: DietaryLabel[] = DIETARY_LABEL;

  constructor(
    public restaurantStore: RestaurantStore,
    public formatDataService: FormatDataService,
    private paymentStore: PaymentStore,
    private cartStore: CartStore,
    private leadTimeService: ItemValidateService,
    private modalController: ModalController,
    private router: Router,
    private itemService: ItemService,
    private dialog: MatDialog,
  ) {
  }

  public get isOnlyOneVariation(): boolean {
    const { variations } = this.product;
    return variations.length === 1;
  }

  public get enlargedImages(): boolean {
    return this.product.info.imageUrl !== null ? this.restaurant?.enlargeProductImages : false;
  }

  public get isUseInventory(): boolean {
    if (!this.chosenVariation) {
      return false;
    }
    return this.chosenVariation.trackInventory && (this.enforceInventory || this.chosenVariation.useInventory);
  }

  public get allowOrder(): boolean {
    return this.isMobileEnabled && (!this.isClosed || this.allowScheduledPickups) && this.isRestaurantHaveEnabledWorkDays;
  }

  public get showInvalid(): boolean {
    return this.showInvalidMessage;
  }

  public get isValid(): boolean {
    return !this.modifiersValidations.find((data) => data.isValid === false);
  }

  public get quantity(): number {
    if (this.cartProduct.item.quantity < 0) {
      this.cartProduct.item.quantity = 1;
    }
    return this.cartProduct && this.cartProduct.item.quantity;
  }

  ngOnInit() {
    this.product = _.cloneDeep(this.defaultProduct);
    this.restaurantStore.isDefaultTax$.subscribe((isDefaultTax: boolean) => this.isDefaultTax = isDefaultTax);
    this.sortVariations();

    this.isSgtPepps = this.router.url.includes('E0V9GD895GK84');
    if (this.cartProduct) {
      this.isEdit = true;
      this.cartProduct = this.copyMenuItemForPayment(this.cartProduct);
      this.setSelectedModifiers();
    } else {
      this.initProduct();
      this.addPreSelectedModifiers();
    }
    for (const group of this.product.modifierGroups) {
      if (group.name.includes('{s}')) {
        for (const modifier of group.modifiers) {
          if (!modifier.name.includes('{s}')) {
            modifier.name += ' {s}';
          }
        }
      }
    }

    if (!this.isModal) {
      const checkExist = setInterval(() => {
        if (document.getElementById('swipeZone')) {
          this.restaurantStore.openItemCardInProgress = false;
          const hammerTime = new Hammer(document.getElementById('swipeZone'));
          hammerTime.get('swipe').set({
            direction: Hammer.DIRECTION_DOWN
          });
          fromEvent(hammerTime, 'swipe').pipe(
            takeWhile(() => this.touchSwipeElement))
            .subscribe(() => {
              this.closeModal();
            });
          clearInterval(checkExist);
        }
      }, 500);
    }
    this.sortModifiers(this.cartProduct.item.variation);
    this.setHiddenModifiers();
    this.estimateThrottling();
  }
  ngOnDestroy() {
    this.product = null;
  }

  private setSelectedModifiers(): void {
    this.product.modifierGroups.forEach(group => {
      this.modifiersValidations.push(new ProductModifierValidation(group.id, this.validateItemModifiers(group)));
      group.modifiers.forEach(modifier => {
        const cartModifier = this.cartProduct.item.modifiers.find(cartMod => cartMod.id === modifier.id);
        cartModifier ? modifier.isSelected = true : modifier.isSelected = false;
      });
    });
  }

  private sortVariations() {
    if (this.isUseInventory) {
      this.sortVariationsByInStock();
    }
  }

  private copyMenuItemForPayment({ locationId, categoryId, itemId, item, isService }: MenuItemForPayment): MenuItemForPayment {
    this.chosenVariation = item.variation;
    this.specialInstructions = item.specialInstructions;
    return new MenuItemForPayment(locationId, categoryId, itemId, new MenuItemData(
      item.id, item.name, item.image, item.quantity, item.variation,
      item.modifiers, item.description, item.totalItemPrice, item.specialInstructions
      ),
      isService
    );
  }

  private initProduct() {
    const filteredVariationList = this.getFilteredVariations(this.product.variations);
    if (filteredVariationList.length === 0) {
      return;
    }
    this.firstVariationIndex = this.product.variations.findIndex((variation: ProductVariation) => variation.id === filteredVariationList[0].id);
    const firstVariationItem = this.product.variations[this.firstVariationIndex];
    this.chosenVariation = this.product.variations[this.firstVariationIndex];
    const productInfo = this.product.info;

    let modifiers: ProductModifier[] = [];
    this.product.modifierGroups.forEach(group => {
      const selectedModifiers: ProductModifier[] = group.modifiers;
      const copiedSelectedModifiers = [];
      for (const modifier of selectedModifiers) {
        copiedSelectedModifiers.push(this.deepCopyModifiers(modifier));
      }
      if (selectedModifiers.length > 0) {
        modifiers = [...modifiers, ...copiedSelectedModifiers];
      } else if (group.selectionType === ModifierSelectionType.single
        && !this.formatDataService.containsKeyword(group.name, 'HIDDEN') && !this.hiddenWithCost(group.modifiers[0])) {
        group.selectionType = ModifierSelectionType.single;
        modifiers = [...modifiers, this.deepCopyModifiers(group.modifiers[0])];
      }
    });

    const menuItem = new MenuItemData(
      firstVariationItem.id,
      productInfo.name,
      productInfo.imageUrl,
      this.defaultQuantity,
      firstVariationItem,
      modifiers,
      productInfo.description
    );
    this.cartProduct = new MenuItemForPayment(
      this.locationId,
      this.product.categoryId,
      productInfo.id,
      menuItem,
      this.product.isService
    );
    this.product.modifierGroups.forEach(group => {
      this.modifiersValidations.push(new ProductModifierValidation(group.id, this.validateItemModifiers(group)));
    });
  }

  public isActiveVariation(variation: ProductVariation): boolean {
    return this.cartProduct && this.cartProduct.item.variation.name === variation.name;
  }

  public isEditingVariation(variation: ProductVariation): boolean {
    return this.cartProduct && this.cartProduct.item.id === variation.id;
  }

  public hiddenWithCost(modifier: ProductModifier): boolean {
    return this.formatDataService.containsKeyword(modifier.name, 'HIDDEN') && modifier.price.amount > 0;
  }

  public getVariationQuantityById(variationId: string): number {
    const productVariation: ProductVariation = this.product.variations.find((variation) => variation.id === variationId);
    return productVariation ? this.getVariationQuantity(productVariation) : Number.MAX_VALUE;
  }

  public getVariationQuantity(variation: ProductVariation): number {
    if (variation.quantityRemaining === null) {
      return Number.MAX_VALUE;
    }
    let cartQuantityToDeduct: number = this.cartStore.getVariationQuantityInCart(variation.id);
    if (this.isEdit && this.isEditingVariation(variation)) {
      cartQuantityToDeduct = 0;
    }
    let currentQuantityToDeduct = 1;
    if (this.isActiveVariation(variation)) {
      currentQuantityToDeduct = this.cartProduct.item.quantity;
    }
    return variation.quantityRemaining - currentQuantityToDeduct - cartQuantityToDeduct;
  }

  public isVariationInStock(variation: ProductVariation): boolean {
    return (this.getVariationQuantity(variation) >= 0 && variation.trackInventory) || !variation.soldOut;
  }

  public isSelectedProductInStock(product: RestaurantProduct): boolean {
    if (!this.isUseInventory && !this.containsSoldOut(product)) {
      return true;
    }
    for (const variation of product.variations) {
      if (this.isActiveVariation(variation) &&
        this.isVariationInStock(variation) &&
        this.currentQuantityRemaining(product) >= 0) {
        return true;
      }
    }
    return this.restaurant.enableSoldOutOrders;
  }

  public containsSoldOut(product: RestaurantProduct): boolean {
    return product.variations.filter(variation => !variation.soldOut || (variation.trackInventory && variation.quantityRemaining > 0)).length === 0;
  }

  public currentQuantityRemaining(product: RestaurantProduct): number {
    for (const variation of product.variations) {
      if (this.isActiveVariation(variation)) {
        return this.getVariationQuantity(variation);
      }
    }
    return 0;
  }

  public isApplyEnforce(product: RestaurantProduct): boolean {
    return this.cartProduct && !this.cartProduct.item.variation.disabled && (!this.isUseInventory || (this.allowOrder && this.currentQuantityRemaining(product) >= 0));
  }

  public isShowPrice(product: RestaurantProduct): boolean {
    return this.cartProduct
      && !this.cartProduct.item.variation.disabled
      && !this.cartProduct.item.variation.soldOut
      && !isModifierSoldOut(product)
      && (this.isUseInventory ?  this.allowOrder && (this.currentQuantityRemaining(product) >= 0 || this.restaurant.enableSoldOutOrders) : this.allowOrder);
  }

  public isAddButtonDisabled(product: RestaurantProduct): boolean {
    return this.cartProduct == null
      || this.cartProduct.item.variation.disabled
      || !this.isSelectedProductInStock(product)
      || (this.isUseInventory && (!this.isSelectedProductInStock(product) || this.currentQuantityRemaining(product) < 0));
  }

  public checkFutureOrder(product: RestaurantProduct): boolean {
    return this.isUseInventory &&
        (!this.isSelectedProductInStock(product) || this.currentQuantityRemaining(product) < 0) && this.restaurant.enableSoldOutOrders;
  }

  public isApplyVariationInStock(variation: any): boolean {
    return variation && (variation.disabled || variation.soldOut || !this.isVariationInStock(variation) && variation.trackInventory && (this.enforceInventory || variation.useInventory));
  }

  public allowSoldOutOrders(isAllow: boolean, variation: any): boolean {
    if (isAllow) {
      if (!this.isVariationInStock(variation)) {
        return !this.restaurant.enableSoldOutOrders;
      }
      return true;
    }
    return isAllow;
  }

  public isMinOrMaxSet(minOrMax: number) {
    return minOrMax !== -1 && minOrMax !== null && minOrMax !== 0;
  }

  public formatModifierGroupRequiredStatus(productModifierGroup: ProductModifierGroup): string {
    if (this.isSelectOneModifier(productModifierGroup)) {
      return 'Required';
    }

    const { defaultMinSelectedModifiers, defaultMaxSelectedModifiers } = productModifierGroup;
    const isMinSet = this.isMinOrMaxSet(defaultMinSelectedModifiers);
    const isMaxSet = this.isMinOrMaxSet(defaultMaxSelectedModifiers);

    if (!isMinSet && !isMaxSet) {
      return 'Optional';
    }

    if (!isMinSet) {
      return `Choose ${defaultMaxSelectedModifiers} or fewer`;
    }

    if (!isMaxSet) {
      return `Choose ${defaultMinSelectedModifiers} or more`;
    }

    if (defaultMinSelectedModifiers === defaultMaxSelectedModifiers) {
      return `Choose exactly ${defaultMinSelectedModifiers}`;
    }

    return `Choose ${defaultMinSelectedModifiers} to ${defaultMaxSelectedModifiers}`;
  }

  public isSelectOneModifier(productModifierGroup: ProductModifierGroup): boolean {
    return productModifierGroup.selectionType === ModifierSelectionType.single;
  }

  public addToCartButtonText(product: RestaurantProduct): string {
    if (!this.allowOrder) {
      return 'Store not currently accepting orders';
    } else if (this.isEdit) {
      return 'Update';
    } else if (this.cartProduct == null || this.cartProduct.item.variation.disabled) {
      return 'Item Unavailable';
    } else if (this.checkFutureOrder(product)) {
      return 'Add to Cart (future orders accepted)';
    } else if (this.isSelectedProductInStock(product) || !this.isApplyVariationInStock(product.variations)) {
      return 'Add to Cart';
    } else if (this.currentQuantityRemaining(product) <= 0) {
      return 'No more available';
    }
    return 'Out of stock';
  }

  public closeModal(): void {
    this.paymentStore.isWaitingUpdate = false;
    if (this.isModal) {
      this.close.emit();
    } else {
      this.modalController.dismiss();
    }
  }

  public isDescriptionAvailable(): boolean {
    return this.product.info.description !== null;
  }

  public chooseVariation(variation: ProductVariation): void {
    this.cartProduct.item.modifiers = [];
    this.addPreSelectedModifiers();
    const index = this.product.variations.indexOf(variation);
    this.cartProduct.item.variation = this.product.variations[index];
    let modifiers: ProductModifier[] = [];
    this.product.modifierGroups.forEach(group => {
      const selectedModifiers: ProductModifier[] = group.modifiers.filter(modifier => this.formatDataService.containsKeyword(modifier.name, 'SELECTED') && !this.hiddenWithCost(modifier));
      const copiedSelectedModifiers = [];
      for (const modifier of selectedModifiers) {
        copiedSelectedModifiers.push(this.deepCopyModifiers(modifier));
      }
      if (selectedModifiers.length > 0) {
        modifiers = [...modifiers, ...copiedSelectedModifiers];
      } else if (group.selectionType === ModifierSelectionType.single
        && !this.formatDataService.containsKeyword(group.name, 'HIDDEN') && !this.hiddenWithCost(group.modifiers[0])) {
        group.selectionType = ModifierSelectionType.single;
        modifiers = [...modifiers, this.deepCopyModifiers(group.modifiers[0])];
      }
    });
    if (variation.trackInventory && (this.enforceInventory || variation.useInventory)) {
      let inventoryQuantity = this.cartProduct.item.quantity;
      const newVariationQuantity = inventoryQuantity + this.getVariationQuantityById(this.cartProduct.item.variation.id);
      if (inventoryQuantity > newVariationQuantity && newVariationQuantity > 0) {
        inventoryQuantity = newVariationQuantity;
        this.variationsInvalidMessage = `(only ${newVariationQuantity} remaining)`;
      } else if (inventoryQuantity <= newVariationQuantity) {
        this.variationsInvalidMessage = '';
      }
      this.cartProduct.item.quantity = inventoryQuantity === 0 ? 1 : inventoryQuantity;
    } else {
      this.variationsInvalidMessage = '';
    }
    this.cartProduct.item.calculateTotalPrice();
    this.cartProduct.item.id = this.product.variations[index].id;
    this.chosenVariation = this.product.variations[index];
    this.setHiddenModifiers();
    this.estimateThrottling();
  }

  public setHiddenModifiers(): void {
    this.product.modifierGroups.map(group => {
      const findGroup = this.chosenVariation?.modifierList.find(chosenGroup => chosenGroup.id === group.id);
      if (findGroup) {
        const activeCount = findGroup.modifiers.filter(filterModif => filterModif.selected).length;
        group.defaultMinSelectedModifiers > activeCount ? group.defaultMinSelectedModifiers = activeCount : null;
        group.minSelectedModifiers > activeCount ? group.defaultMinSelectedModifiers = activeCount : null;
      }
      group.modifiers.map(groupModifier => {
        if (findGroup) {
          const findModifier = findGroup.modifiers.find(modifier => groupModifier.id === modifier.id);
          if (findModifier) {
            groupModifier.isShow = findModifier.selected;
          }
        }
      });
    });
  }

  public checkCustomModifiersGroup(modifierGroup: ProductModifierGroup): boolean {
    const modifGroup = this.chosenVariation?.modifierList.find(group => group.id === modifierGroup.id);
    return modifGroup?.modifiers.every(custModif => !custModif.selected);
  }

  public chooseModifier(event, modifiersListIndex, modifierIndex): void {
    if (this.cartProduct == null) {
      return;
    }
    const isChecked = event.target.checked;
    const chosenModifier = this.product.modifierGroups[modifiersListIndex].modifiers[modifierIndex];
    chosenModifier.isSelected = isChecked;
    const modifiers = this.cartProduct.item.modifiers;
    this.cartProduct.item.modifiers = isChecked
      ? [...modifiers, this.deepCopyModifiers(chosenModifier)]
      : this.removeModifierFromList(modifiers, this.deepCopyModifiers(chosenModifier));
    this.cartProduct.item.calculateTotalPrice();
    this.modifiersValidations.map((group) => {
      if (group.id === this.product.modifierGroups[modifiersListIndex].id) {
        group.isValid = this.validateItemModifiers(this.product.modifierGroups[modifiersListIndex]);
      }
    });
  }

  public isModifierAllow(group: ProductModifierGroup): boolean {
    if (group.hiddenFromCustomer) {
      return false;
    }
    const selectedItemCount = group.modifiers.reduce((count, modifier) => {
      return modifier.isSelected === true ? count + 1 : count;
    }, 0);
    const max = group.maxSelectedModifiers !== -1 && group.maxSelectedModifiers !== null && group.maxSelectedModifiers <= selectedItemCount;
    this.setValidationModifiers(selectedItemCount, group);
    return max;
  }

  private setValidationModifiers(selectedItemCount: number, group: ProductModifierGroup): void {
    const isMaxDefined = group.defaultMaxSelectedModifiers !== -1 && group.defaultMaxSelectedModifiers !== null;
    const isMinDefined = group.defaultMinSelectedModifiers !== -1 && group.defaultMinSelectedModifiers !== null;

    const max = isMaxDefined ? group.defaultMaxSelectedModifiers >= selectedItemCount : true;
    const min = isMinDefined ? group.defaultMinSelectedModifiers <= selectedItemCount : true;

    const selectedModifier = this.modifiersValidations.find(modif => modif.id === group.id);

    if (selectedModifier) {
      selectedModifier.isValid = max && min;
    }
  }


  public modifierSliderChoose(variant, modifiersListIndex, modifierIndex) {
    if (this.cartProduct == null) {
      return;
    }
    const chosenModifier = this.product.modifierGroups[modifiersListIndex].modifiers[modifierIndex];
    const modifierInCart = this.cartProduct.item.modifiers.find((modifier) => modifier.id === chosenModifier.id);
    switch (variant) {
      case 1:
        modifierInCart.name = modifierInCart.name
          .replace('{s}', ' {Light}').trim()
          .replace('{Regular}', ' {Light}').trim()
          .replace('{Extra}', ' {Light}');
        modifierInCart.price.amount = Math.floor(chosenModifier.price.amount / 2);
        break;
      case 2:
        modifierInCart.name = modifierInCart.name
          .replace('{s}', ' {Regular}').trim()
          .replace('{Light}', ' {Regular}').trim()
          .replace('{Extra}', ' {Regular}');
        modifierInCart.price.amount = chosenModifier.price.amount;
        break;
      case 3:
        modifierInCart.name = modifierInCart.name
          .replace('{s}', ' {Extra}').trim()
          .replace('{Light}', ' {Extra}').trim()
          .replace('{Regular}', ' {Extra}');
        modifierInCart.price.amount = Math.floor(chosenModifier.price.amount * 1.5);
        break;
    }
    this.cartProduct.item.calculateTotalPrice();
  }

  public isChosenModifier(modifiersListIndex, modifierIndex): boolean {
    if (this.cartProduct == null) {
      return false;
    }
    const chosenModifier = this.product.modifierGroups[modifiersListIndex]
      .modifiers[modifierIndex];
    return this.cartProduct.item.modifiers.filter(modifier => modifier.id === chosenModifier.id).length > 0;
  }

  public cleanedName(name: string): string {
    return name.replace('(*)', '').replace('(#)', '').replace('(^)', '').replace('{s}', '').trim();
  }

  public chooseAndRemoveModifiers(event, modifiersListIndex, modifierIndex, modifier: ProductModifier): void {
    if (this.cartProduct == null) {
      return;
    }
    const currentModifierSet = this.product.modifierGroups[modifiersListIndex].modifiers;
    currentModifierSet.map(current => current.isSelected = false);
    modifier.isSelected = true;
    for (let i = 0; i < currentModifierSet.length; ++i) {
      const modifiers = this.cartProduct.item.modifiers;
      this.cartProduct.item.modifiers = i === modifierIndex && !this.findDuplicateModifiers(currentModifierSet[modifierIndex])
        ? [...modifiers, this.deepCopyModifiers(currentModifierSet[i])]
        : this.removeModifierFromList(modifiers, this.deepCopyModifiers(currentModifierSet[i]));
    }
    this.cartProduct.item.calculateTotalPrice();
  }

  public changeQuantity(shouldIncrease: boolean): void {
    if (shouldIncrease
      && this.currentQuantityRemaining(this.product) <= 0
      && this.isUseInventory
      && !this.restaurant.enableSoldOutOrders) {
      this.variationsInvalidMessage = '(no more available)';
    } else if (shouldIncrease) {
      this.cartProduct.item.quantity++;
      this.variationsInvalidMessage = '';
    } else if (this.cartProduct.item.quantity > this.quantityThreshHold) {
      this.cartProduct.item.quantity--;
      this.variationsInvalidMessage = '';
    }
    this.estimateThrottling();
  }

  public changeSpecialInstructions(): void {
    this.cartProduct.item.specialInstructions = this.specialInstructions;
  }

  public validateItemModifiers(modifierGroup: ProductModifierGroup): boolean {
    if (this.cartProduct == null) {
      return false;
    }

    if (modifierGroup.hiddenFromCustomer) {
      return true;
    }

    const chosenModifiersFromGroup = this.cartProduct.item.modifiers.filter(menuItem => {
      const modifier = modifierGroup.modifiers.find(mod => mod.id === menuItem.id);
      return modifier != null && modifier.isShow;
    });

    if (modifierGroup.selectionType === ModifierSelectionType.single || this.formatDataService.containsKeyword(modifierGroup.name, 'HIDDEN')) {
      return true;
    }

    const { defaultMinSelectedModifiers, defaultMaxSelectedModifiers, minSelectedModifiers, maxSelectedModifiers } = modifierGroup;

    if (!this.isMinOrMaxSet(defaultMinSelectedModifiers) && !this.isMinOrMaxSet(defaultMaxSelectedModifiers)) {
      return true;
    }


    if (!this.isMinOrMaxSet(defaultMinSelectedModifiers) && this.isMinOrMaxSet(defaultMaxSelectedModifiers)) {
      return chosenModifiersFromGroup.length <= maxSelectedModifiers;
    }

    if (this.isMinOrMaxSet(defaultMinSelectedModifiers) && !this.isMinOrMaxSet(defaultMaxSelectedModifiers)) {
      return chosenModifiersFromGroup.length >= minSelectedModifiers;
    }

    if (minSelectedModifiers === maxSelectedModifiers) {
      return chosenModifiersFromGroup.length === minSelectedModifiers;
    }

    return chosenModifiersFromGroup.length >= defaultMinSelectedModifiers && chosenModifiersFromGroup.length <= defaultMaxSelectedModifiers;
  }

  public findModifierPrice(modifier) {
    if (this.cartProduct == null) {
      return;
    }
    const modifierInCart = this.cartProduct.item.modifiers.find((mod) => mod.id === modifier.id);
    if (modifierInCart) {
      return modifierInCart.price.formattedAmount;
    }
    return modifier.price.formattedAmount;
  }

  public findModifierSliderParam(modifier): string {
    const modifierInCart = this.cartProduct.item.modifiers.find((mod) => mod.id === modifier.id);

    if (modifierInCart) {
      if (modifierInCart.name.includes('{Light}')) {
        return 'Light';
      } else if (modifierInCart.name.includes('{Regular}')) {
        return 'Regular';
      } else if (modifierInCart.name.includes('{Extra}')) {
        return 'Extra';
      }
    }
    return 'Regular';
  }

  public getFormattedPrice(price: string | number, quantity = 1,isModifier=false): string {
    if(this.isDisplayKyooFee() && !isModifier){
      return ((+price * quantity) + (this.restaurant.kyooFee/100)).toFixed(2);
    }
    return (+price * quantity).toFixed(2);
  }

  public getFilteredVariations(itemVariations: ProductVariation[]): ProductVariation[] {
    const filteredVariationList: ProductVariation[] = [];
    for (const variation of itemVariations) {
      variation.disabled = false;
      let variationHidden = false;
      // Hide or disable unorderable time-restricted items during service for ASAP orders
      if ((this.restaurant?.isAcceptingOrders
        && !this.allowScheduledPickups
        && !this.validateVariationAvailableTimes(variation))) {
        variation.disabled = true;
        variationHidden = !this.restaurant.showAllTimeRestrictedItems;
      }
      // Hide or disable items with incorrect fulfillment types
      if (variation.isFulfillmentRestrictionEnabled
        && !this.leadTimeService.validFulfillments(variation, this.restaurantStore.enabledFulfillments)) {
        variation.disabled = true;
        variationHidden = variationHidden || !this.restaurant.showAllFulfillmentRestrictedItems;
      }
      filteredVariationList.push(variation);
    }
    return filteredVariationList;
  }

  public addItemToCart(cartProduct: MenuItemForPayment, product: RestaurantProduct): void {
    cartProduct.throttlingEstimation = this.chosenVariation.throttlingEstimation;
    if (this.restaurant.enforceAcceptOrderThrottling && this.restaurant.isOrderThrottling && cartProduct.throttlingEstimation) {
      this.openThrottlingModal(cartProduct, product);
      return;
    }
    this.addCartItem(cartProduct, product);
  }

  private openThrottlingModal(cartProduct: MenuItemForPayment, product: RestaurantProduct): void {
    const dialogRef = this.dialog.open(ThrottlingConfirmationComponent, {
      data: {
        cartProduct: cartProduct,
      },
      height: 'fit-content',
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addCartItem(cartProduct, product);
      }
    });
  }

  private addCartItem(cartProduct: MenuItemForPayment, product: RestaurantProduct): void {
    if (this.checkFutureOrder(product)) {
      cartProduct.isFutureOrder = true;
    }
    if (this.isValid) {
      if (this.isModal) {
        this.addItem.emit(cartProduct);
      } else {
        this.modalController.dismiss({ cartProduct });
      }
      this.sortVariations();
    } else {
      this.scrollToFirstInvalidModifier();
      this.showInvalidMessage = true;
    }
  }

  public isModifiersHidden(modifiers: ProductModifier[]): boolean {
    return modifiers.every(modifier => !modifier.isShow);
  }

  public isDisplayKyooFee():boolean {
    return ((this.restaurant && this.restaurant.displayKyooFeeOnMenu && this.cartStore && this.cartStore.totalQuantity == 0)
      || this.restaurant && this.restaurant.displayKyooFeeOnMenu && this.cartStore && this.cartStore.items && this.cartStore.items[0].item.id == this.cartProduct.item.id && this.isEdit);
  }

  private validateVariationAvailableTimes(variation: ProductVariation): boolean {
    let isValidLeadTime = true;
    let isValidWeekDay = true;
    if (variation == null) {
      return false;
    }
    if (variation.isLeadTimesEnabled) {
      isValidLeadTime = this.leadTimeService.validLeadTime(variation, this.hoursOfOperation);
    }
    if (variation.isDailyRestrictionEnabled) {
      isValidWeekDay = this.leadTimeService.validDayRestriction(variation);
    }
    return isValidLeadTime && isValidWeekDay;
  }

  private scrollToFirstInvalidModifier(): void {
    const modifierIndex = this.modifiersValidations.find((data) => data.isValid === false);
    if (modifierIndex) {
      const modifier = document.getElementById(`modifier ${modifierIndex.id}`);
      if (this.isModal) {
        modifier.scrollIntoView({ behavior: 'smooth' });
      } else {
        this.content.scrollToPoint(0, modifier.offsetTop - 150, 200);
      }
    }
  }

  private sortVariationsByInStock(): void {
    this.product.variations.sort((a, b) =>
      this.getVariationQuantity(a) >= 0 && this.getVariationQuantity(b) < 0 ? -1 : 1
    );
  }

  private removeModifierFromList(modifiers, chosenModifier): ProductModifier[] {
    return modifiers.filter(modifier => {
      return modifier.id !== chosenModifier.id;
    });
  }

  private deepCopyModifiers(modifier: MenuItem): MenuItem {
    return new MenuItem(modifier.name, modifier.id, new Price(modifier.price.amount, modifier.price.currency));
  }
  private sortModifiers(productVariation: ProductVariation): void {
    this.setDefaultProductShow();
    // if (productVariation.modifiersDisabled) {
    //   productVariation.modifierList.map(modifierList => {
    //     if (modifierList.selected) {
    //       modifierList.modifiers.map(unSortModifier => {
    //         this.product.modifierGroups.map(groupModifier => {
    //           groupModifier.modifiers.map((modifier) => {
    //             if (unSortModifier.id === modifier.id && !unSortModifier.selected) {
    //               modifier.isShow = false;
    //             }
    //           });
    //         });
    //       });
    //     } else {
    //       this.product.modifierGroups.map(groupModifier => {
    //         if (groupModifier.id === modifierList.id) {
    //           groupModifier.modifiers.map((modifier) => {
    //             modifier.isShow = false;
    //           });
    //         }
    //       });
    //     }
    //   });
    // }
    this.setSingleModifier();
    this.setMinMaxModifiers();
    this.cartProduct.item.calculateTotalPrice();
    this.checkIsShowAndSetValid();
  }
  private setDefaultProductShow(): void {
    this.product.modifierGroups.map(groupModifier => {
      groupModifier.modifiers.map((modifier) => {
       modifier.isShow = true;
      });
    });
  }
  private setSingleModifier(): void {
    this.product.modifierGroups.map(groupModifier => {
      groupModifier.modifiers.map((modifier) => {
        const findDisabled = this.cartProduct.item.modifiers.some(findModifier => findModifier.id === modifier.id && !modifier.isShow);
        if (findDisabled) {
          this.cartProduct.item.modifiers = [];
        }
        if (groupModifier.selectionType === ModifierSelectionType.single
          && this.cartProduct.item.modifiers.length === 0 && modifier.isShow) {
          this.cartProduct.item.modifiers.push(modifier);
        }
      });
    });
  }

  private setMinMaxModifiers(): void {
    this.product.modifierGroups.map(group => {
      group.minSelectedModifiers = group.defaultMinSelectedModifiers;
      group.maxSelectedModifiers = group.defaultMaxSelectedModifiers;
      let isShow = 0;
      group.modifiers.forEach(modifier => {
        if (modifier.isShow) {
          isShow++;
        }
      });
      if (group.maxSelectedModifiers > isShow) {
        group.maxSelectedModifiers = isShow;
      }
      if (group.minSelectedModifiers > group.maxSelectedModifiers) {
        const number = group.maxSelectedModifiers - group.minSelectedModifiers;
        number < 1 ? group.minSelectedModifiers = 1 : group.minSelectedModifiers = number;
      }
    });
  }
  private findDuplicateModifiers(productModifier: ProductModifier): boolean {
    return this.cartProduct.item.modifiers.some(modifier => modifier.id === productModifier.id);
  }

  private addPreSelectedModifiers(): void {
    const newModifiers = [];
    this.product.modifierGroups.map( modifierGroup => {
      modifierGroup.modifiers.map(modif => {
        if (modif.isSelected || modif.onByDefault) {
          newModifiers.push(modif);
        }
      });
      const isSelectedModifiers = modifierGroup.modifiers.every(modif => modif.isSelected === false);
      if (modifierGroup.selectionType === ModifierSelectionType.single && isSelectedModifiers) {
        modifierGroup.modifiers[0].isSelected = true;
        newModifiers.push(modifierGroup.modifiers[0]);
      }
    });
    if (this.cartProduct?.item) {
      this.cartProduct.item.modifiers = newModifiers;
      this.cartProduct.item.calculateTotalPrice();
    }
  }
  private checkIsShowAndSetValid(): void {
    this.product.modifierGroups.forEach(group => {
      if (group.modifiers.every(modifier => !modifier.isShow)) {
        this.modifiersValidations.forEach(variation => {
          if (variation.id === group.id) {
            variation.isValid = true;
          }
        });
      }
    });
  }

  private estimateThrottling(): void {
    if (this.restaurant.isOrderThrottling) {
      const variations = [new EstimateOrder(this.chosenVariation.id, this.quantity.toString())];
      this.itemService.estimateThrottling(this.product.locationId, variations).subscribe(estimateOrder => {
        this.product?.variations.map(variation => {
          if (variation.id === estimateOrder[0].catalog_object_id) {
            variation.throttlingEstimation = estimateOrder[0].duration;
          }
        });
        this.chosenVariation.throttlingEstimation = estimateOrder.find(estimation => estimation.catalog_object_id === this.chosenVariation.id).duration;
      });
    }
  }

  protected readonly isModifierSoldOut = isModifierSoldOut;
}
